import { Divider, makeStyles, shorthands, tokens, Spinner } from '@fluentui/react-components';
import { FC, useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useNotification } from '../../Contexts/NotificationContext';
import { useUserContext } from '../../Contexts/UserContext';
import { useConfirm } from '../../Contexts/hooks';
import { ActionTypes, ConversationPatch, IConversation, UserPreferences, Conversation } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { useInfiniteConversations, getDefaultConversationPageSize } from '../../Hooks/useInfiniteConversations';
import { ChatSpecificRoute, buildRoute } from '../../Routes';
import { clearConversationsAction, removeConversationAction, updateConversationAction, appendConversationsAction, addConversationAction, setConversationsCursorAction } from '../../store/actions/conversationActions';
import { useConversations } from '../../store/hooks';
import MenuConversations from './MenuConversations';
import MenuBottom from './MenuBottom';
import { Input, Label } from '@fluentui/react-components';
import { useUserPreferences } from '../../store/hooks';
import { setUserPreferencesAction } from '../../store/actions/userPreferencesAction';
import { AppConversation } from '../../store/models/conversation';
import { formatDateToDDMMYYYY, formatDateToDDMMYYYYHHMMSS } from '../../Utils/datetime';

const useStyles = makeStyles({
    bottom: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalM,
    },
    smallDivider: {
        flexGrow: 0,
        height: '1.5rem',
    },
    searchBarWrapper: {
        // Match the positioning of the SyGPT Tools button container
        padding: 0,
        margin: 0,
        marginTop: '0.5rem',
        marginBottom: '0.5rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
    },
    searchInput: {
        minWidth: 0,
        width: '100%', // Take full width
        margin: 0, // Remove all margins to match other menu items
        borderRadius: tokens.borderRadiusLarge,
        background: tokens.colorNeutralBackground2,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        boxShadow: 'none !important',
        fontSize: tokens.fontSizeBase300,
        // Keep other properties
        paddingLeft: '1.5rem', /* Exactly match section label padding */
        paddingRight: tokens.spacingHorizontalM,
        height: '48px',  // Match the SyGPT Tools button height
        fontFamily: 'var(--DefaultFontFamily)',
        outline: 'none',
        caretColor: tokens.colorBrandForeground1,
        
        /* Target the actual input element inside to ensure proper alignment */
        '& input': {
            paddingLeft: '1.5rem !important', /* Increase left margin for better alignment */
        },
        
        '& .fui-Input__contentAfter': {
            /* Ensure the contentAfter container aligns the icon properly */
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            boxSizing: 'border-box',
        },
        
        '&:focus': {
            border: `1px solid ${tokens.colorNeutralStroke2} !important`,
            boxShadow: 'none !important',
            outline: 'none !important',
            background: tokens.colorNeutralBackground2,
        },
        '&:active': {
            border: `1px solid ${tokens.colorNeutralStroke2} !important`,
            boxShadow: 'none !important',
            outline: 'none !important',
            background: tokens.colorNeutralBackground2,
        },
        '&:focus-visible': {
            outline: 'none !important',
            boxShadow: 'none !important',
            border: `1px solid ${tokens.colorNeutralStroke2} !important`,
        },
        '&::placeholder': {
            color: tokens.colorNeutralForeground3,
            fontFamily: 'var(--DefaultFontFamily)',
            paddingLeft: '0 !important', /* Reset any default placeholder padding */
        },
        '&::after': {
            display: 'none !important',
            content: 'none !important',
            borderBottom: 'none !important',
        },
        '&:focus::after': {
            display: 'none !important',
            content: 'none !important',
        },
        '& .fui-Input': {
            '&::after': {
                display: 'none !important',
                content: 'none !important',
            }
        },
        '& .fui-Input__input': {
            /* Direct targeting of the input element */
            paddingLeft: '0.8rem !important', /* Add consistent left margin */
        },
        '& div::after': {
            display: 'none !important',
            content: 'none !important',
            borderBottom: 'none !important',
        },
        '& .fui-Input__input:focus + div::after': {
            display: 'none !important',
        },
    },
    lensIcon: {
        background: 'transparent',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: tokens.colorNeutralForeground3,
        border: 'none',
        // Fixed dimensions for consistent appearance
        width: '16px',
        height: '16px',
    },
    sectionLabel: {
        marginTop: 0,
        marginBottom: 0,
        display: 'block',
        textAlign: 'left',
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase400,
        color: tokens.colorBrandForeground1,
        letterSpacing: '0.01em',
        paddingLeft: '1.5rem',
        paddingRight: '1.5rem',
        background: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusMedium,
        lineHeight: 1.2,
    },
    sectionWrapper: {
        margin: 0,
        padding: 0,
        // Remove scrolling from this wrapper - parent container handles it
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
    },
    autoHideScrollbar: {
        scrollbarWidth: 'thin',
        scrollbarColor: 'transparent transparent',
        '&:hover': {
            scrollbarColor: '#888 #222',
        },
        '&::-webkit-scrollbar': {
            width: '8px',
            background: 'transparent',
        },
        '&:hover::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'transparent',
        },
    },
});

interface AppMenuContentProps {
    onCloseMenuRequired?: () => void;
}
export const AppMenuContent: FC<AppMenuContentProps> = (props: AppMenuContentProps) => {
    const { onCloseMenuRequired } = props;
    const classes = useStyles();
    const { userProfile } = useUserContext();
    const { t } = useTranslation();
    const { activeChat, chats, conversationsCursor, dispatch } = useConversations();
    const navigate = useNavigate();
    const { dispatchNotification } = useNotification();
    const confirm = useConfirm();
    const [activeSearchText, setActiveSearchText] = useState(''); // Only state for actual search
    const [searchResults, setSearchResults] = useState<AppConversation[]>([]); // Store server search results
    const [isSearching, setIsSearching] = useState(false); // Loading state for search
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null); // Direct ref to input element
    const conversationsScrollContainerRef = useRef<HTMLDivElement>(null); // Ref to conversations scroll container

    // Use infinite query for loading more conversations
    // Disable automatic fetching to prevent double-load on startup
    // (Router.tsx handles initial load, this hook only handles "Load More")
    // Use the cursor from Redux to continue pagination from where Router.tsx left off
    const {
        data: infiniteData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteConversations({
        projectId: null,
        limit: getDefaultConversationPageSize(),
        enabled: false, // Never auto-fetch, only fetch when user clicks "Load More"
        initialCursor: conversationsCursor, // Start from where Router.tsx left off
    });

    // Determine if there might be more conversations to load
    // Show "Load More" if:
    // 1. React Query has determined there's a next page (hasNextPage === true)
    // 2. OR we have a cursor from initial load (before any infinite query pages are fetched)
    // We check if infiniteData has pages - if not, the query hasn't run yet, so use cursor
    const hasMoreConversations = hasNextPage === true || (!infiniteData?.pages?.length && conversationsCursor !== undefined);
    
    // Track the number of pages we've already processed to avoid re-processing
    const processedPagesRef = useRef(0);

    // Sync infinite query data with Redux store
    useEffect(() => {
        if (infiniteData?.pages && infiniteData.pages.length > processedPagesRef.current) {
            // Only process new pages that haven't been processed yet
            const newPages = infiniteData.pages.slice(processedPagesRef.current);
            const newConversations: AppConversation[] = [];
            
            newPages.forEach(page => {
                if (page.conversations) {
                    page.conversations.forEach(conv => {
                        newConversations.push({
                            ...conv,
                            active: false,
                            draft: false,
                            prompts: conv.prompts?.map((p) => ({ ...p, aborted: p.answer === null }))
                        });
                    });
                }
            });
            
            if (newConversations.length > 0) {
                dispatch(appendConversationsAction(newConversations));
            }
            
            // Update the cursor from the last page loaded
            const lastPage = infiniteData.pages[infiniteData.pages.length - 1];
            if (lastPage) {
                dispatch(setConversationsCursorAction(lastPage.nextCursor ?? undefined));
            }
            
            // Update the count of processed pages
            processedPagesRef.current = infiniteData.pages.length;
        }
    }, [infiniteData, dispatch]);


    // Filter conversations: match search text in title OR any prompt content,
    // then sort by number of found term occurrences (descending), so conversations with more matches appear first
    const filteredChats = useMemo(() => {
        if (!activeSearchText.trim()) {
            // No sorting - maintain pagination order from server
            return chats.filter((c) => !c.draft);
        }
        // Use server-returned search results instead of client-side filtering
        return searchResults.filter((c) => !c.draft);
    }, [chats, activeSearchText, searchResults]);

    // Partition filteredChats into two groups for search: found in title, found in prompts
    const [foundInTitle, foundInPrompts] = useMemo(() => {
        if (!activeSearchText.trim()) return [[], []];
        
        const searchLower = activeSearchText.toLowerCase();
        const inTitle: AppConversation[] = [];
        const inPrompts: AppConversation[] = [];
        
        searchResults.forEach((conversation) => {
            // Check if search term is in the title
            const titleMatch = conversation.title?.toLowerCase().includes(searchLower);
            
            // Check if search term is in any prompt
            const promptMatch = conversation.prompts?.some(prompt => 
                prompt.query?.toLowerCase().includes(searchLower) || 
                prompt.answer?.toLowerCase().includes(searchLower)
            );
            
            // Add to appropriate section (title takes priority)
            if (titleMatch) {
                inTitle.push(conversation);
            } else if (promptMatch) {
                inPrompts.push(conversation);
            }
        });
        
        return [inTitle, inPrompts];
    }, [searchResults, activeSearchText]);

    const handleClearConversations = () => {
        confirm({
            message: '',
            show: true,
            title: t('menu.persona.confirmClearConversations'),
            cancelText: t('No'),
            confirmText: t('Yes'),
        }).then(() => {
            performEnterpriseGPTApiCall(async (client, signal) => {
                try {
                    await client.deleteAllUserConversations(signal);
                    dispatch(clearConversationsAction());
                } catch {
                    dispatchNotification('error.api.delete.conversations', 'error');
                }
            });
        });
    };

    const handleRemoveConversation = (id: string) => {
        confirm({
            message: '',
            show: true,
            title: t('menu.conversations.actions.remove.confirm'),
            cancelText: t('No'),
            confirmText: t('Yes'),
        }).then(() => {
            performEnterpriseGPTApiCall(async (client, cancelToken) => {
                try {
                    await client.deleteConversation(id, cancelToken);
                    dispatch(removeConversationAction(id));
                    // Also remove from search results if we're in search mode
                    if (activeSearchText.trim()) {
                        setSearchResults(prev => prev.filter(c => c.id !== id));
                    }
                } catch {
                    dispatchNotification('error.api.delete.conversation', 'error');
                }
            });
        });
    };

    const handleConversationTitleChange = (id: string, title: string) => {
        if (!activeChat) {
            return;
        }

        const patch = new ConversationPatch({
            ...chats.find((c) => c.id === id),
            title,
            actionType: ActionTypes.RenameConversation
        });

        performEnterpriseGPTApiCall(async (client, cancelToken) => {
            try {
                const patched = await client.patchConversation(id, patch, cancelToken);
                dispatch(updateConversationAction({ id: patched.id, patch }));
            } catch {
                dispatchNotification('error.api.patch.conversation', 'error');
            }
        });
    };

    const handleConversationSelected = async (id: string) => {

        // Find the conversation in the store, or in search results if we're searching
        let conversation = chats.find(c => c.id === id);
        
        // Determine if we need to load full conversation data
        let needsFullLoad = false;
        
        // If not found in chats and we have search results, check there
        if (!conversation && activeSearchText.trim()) {
            const searchResult = searchResults.find(c => c.id === id);
            if (searchResult) {
                conversation = searchResult;
                needsFullLoad = searchResult.fullyLoaded !== true;
            }
        } else if (conversation) {
            // Check if existing conversation needs full load
            // A conversation needs full load if:
            // 1. It's explicitly marked as not fully loaded
            // 2. OR it doesn't have prompts loaded (undefined or null)
            needsFullLoad = conversation.fullyLoaded !== true || !conversation.prompts;
        }
        
        // If conversation not found at all, return early
        if (!conversation) {
            console.error('Conversation not found:', id);
            return;
        }
        
        // Load full conversation data from server if needed
        if (needsFullLoad) {
            const fullConversationData = await performEnterpriseGPTApiCall(async (client, signal) => {
                const fullConversation = await client.getConversationById(id, signal);
                if (!fullConversation) {
                    throw new Error('Conversation not found or empty response');
                }
                
                const appConversation: AppConversation = {
                    ...fullConversation,
                    active: false,
                    draft: false,
                    prompts: fullConversation.prompts?.map((p) => ({ ...p, aborted: p.answer === null })),
                    fullyLoaded: true // Mark as fully loaded
                };
                
                return appConversation;
            });
            
            if (!fullConversationData) {
                console.error('Failed to load full conversation:', id);
                dispatchNotification('error.api.get.fullConversation', 'error');
                return; // Don't navigate if loading failed
            }
            
            // Update the store with the fully-loaded conversation
            // Use addConversationAction if conversation wasn't in chats originally, otherwise use updateConversationAction
            const existsInChats = chats.find(c => c.id === id);
            if (existsInChats) {
                dispatch(updateConversationAction({ 
                    id: fullConversationData.id, 
                    patch: fullConversationData
                }));
            } else {
                dispatch(addConversationAction(fullConversationData));
            }
            
            conversation = fullConversationData;
        }
        
        // Log conversation details for debugging
        if (conversation) {
            const lastUpdate = formatDateToDDMMYYYYHHMMSS(conversation?.lastUpdateOn);
            //console.log('Selecting conversation:', conversation?.title, 'Last Changed on:', lastUpdate, 'Prompts:', conversation?.prompts?.length);
        }
        
        // Navigate to the conversation
        navigate(buildRoute(ChatSpecificRoute, { id }));
        // Close menu only in mobile mode when a conversation is selected
        if (onCloseMenuRequired) {
            onCloseMenuRequired();
        }
    };

    const { userPreferences, dispatch: userPreferencesDispatch } = useUserPreferences();

    const handlePinToggle = (id: string, pinned: boolean) => {
        performEnterpriseGPTApiCall(async (client) => {
            try {
                const existing: string[] = (userPreferences as any)?.pinnedConversations ?? [];
                let updatedPinned: string[];
                if (pinned) {
                    updatedPinned = Array.from(new Set([...(existing ?? []), id]));
                } else {
                    updatedPinned = (existing ?? []).filter(x => x !== id);
                }

                const updatedUserPreferences: any = {
                    ...userPreferences,
                    pinnedConversations: updatedPinned
                };

                // Optimistically update UI
                userPreferencesDispatch(setUserPreferencesAction(updatedUserPreferences));

                await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
            } catch (e) {
                dispatchNotification('error.api.patch.userPreferences', 'error');
            }
        });
    };

    const handleDisenroll = () => {
        confirm({
            message: t('menu.persona.disenrollInfo'),
            show: true,
            title: t('menu.persona.disenrollConfirm'),
            cancelText: t('No'),
            confirmText: t('Yes'),
        }).then(() => {
            performEnterpriseGPTApiCall(async (client, cancelToken) => {
                try {
                    await client.unenroll(cancelToken);
                    document.location.reload();
                } catch {
                    dispatchNotification('error.api.delete.enrollment', 'error');
                }
            });
        });
    };

    // --- Server-side search behavior ---
    // Trigger search on Enter or search button click - call server API
    const handleSearch = useCallback(() => {
        if (searchInputRef.current) {
            const searchText = searchInputRef.current.value;
            if (!searchText.trim()) {
                setActiveSearchText('');
                setSearchResults([]);
                setIsSearching(false);
                return;
            }
            
            setIsSearching(true);
            performEnterpriseGPTApiCall(async (client, signal) => {
                try {
                    const results = await client.getConversationsFiltered(searchText, false, signal);
                    const appConversations: AppConversation[] = (results || []).map(conv => ({
                        ...conv,
                        active: false,
                        draft: false,
                        fullyLoaded: false, // Mark as not fully loaded since we used fullLoad: false
                        prompts: conv.prompts?.map((p) => ({ ...p, aborted: p.answer === null }))
                    }));
                    setSearchResults(appConversations);
                    setActiveSearchText(searchText);
                } catch (error) {
                    console.error('Search failed:', error);
                    dispatchNotification('error.api.search.conversations', 'error');
                    setSearchResults([]);
                } finally {
                    setIsSearching(false);
                }
            });
        }
    }, [dispatchNotification]);

    const handleFormSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        handleSearch();
    }, [handleSearch]);

    // Detect when input is cleared to reset search results
    const handleInputChange = useCallback((_: unknown, data: { value: string }) => {
        if (data.value === '') {
            setActiveSearchText('');
        }
    }, []);

    const handleClearSearch = useCallback(() => {
        if (searchInputRef.current) {
            searchInputRef.current.value = '';
        }
        setActiveSearchText('');
        setSearchResults([]);
        setIsSearching(false);
    }, []);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', overflow: 'hidden' }}>
            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}
            </style>
            {/* Search box for conversations - classic search */}
            <div ref={searchContainerRef} className={classes.searchBarWrapper} style={{ position: 'relative' }}>
                <form onSubmit={handleFormSubmit} style={{ width: '100%' }}>
                    <Input
                        ref={searchInputRef as any}
                        placeholder="Search in Conversations…"
                        defaultValue=""
                        onChange={handleInputChange}
                        disabled={isSearching}
                        size="large"
                        style={{ 
                            paddingLeft: 0
                        }}
                        contentAfter={
                            <div 
                                onClick={!isSearching ? handleSearch : undefined}
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    height: '100%',
                                    cursor: isSearching ? 'default' : 'pointer'
                                }}
                            >
                                {isSearching ? (
                                    <Spinner size="tiny" />
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 22 22" fill="none">
                                        <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                        <rect x="16" y="16" width="7" height="2" rx="1" transform="rotate(45 16 16)" fill="currentColor"/>
                                    </svg>
                                )}
                            </div>
                        }
                        className={classes.searchInput}
                        autoComplete="off"
                    />
                </form>
            </div>
            
            {/* Conversations container - takes up remaining space */}
            <div
                ref={conversationsScrollContainerRef}
                style={{ flex: '1 1 auto', overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}
                className={classes.autoHideScrollbar}
            >
                {activeSearchText.trim() && (
                    <div className={classes.sectionWrapper}>
                        <Label className={classes.sectionLabel}>
                            In the conversation title ({foundInTitle.length})
                        </Label>
                        {isSearching && (
                            <Label size="small" style={{ marginLeft: 16, marginTop: 16, marginBottom: 4, display: 'block' }}>
                                Searching...
                            </Label>
                        )}
                        {!isSearching && foundInTitle.length > 0 && (
                            <MenuConversations
                                items={foundInTitle}
                                selectedItem={activeChat}
                                onRemoveConversation={handleRemoveConversation}
                                onConversationTitleChange={handleConversationTitleChange}
                                onConversationSelected={handleConversationSelected}
                                hidePinned={true}
                                onPinToggle={handlePinToggle}
                            />
                        )}
                        <Label className={classes.sectionLabel} style={{ marginTop: 0, marginBottom: 0 }}>
                            In the conversation prompts ({foundInPrompts.length})
                        </Label>
                        {!isSearching && foundInPrompts.length > 0 && (
                            <MenuConversations
                                items={foundInPrompts}
                                selectedItem={activeChat}
                                onRemoveConversation={handleRemoveConversation}
                                onConversationTitleChange={handleConversationTitleChange}
                                onConversationSelected={handleConversationSelected}
                                hidePinned={true}
                                onPinToggle={handlePinToggle}
                            />
                        )}
                        {!isSearching && foundInTitle.length === 0 && foundInPrompts.length === 0 && (
                            <Label size="small" style={{ marginLeft: 16, marginTop: 16, marginBottom: 4, display: 'block' }}>
                                No results found.
                            </Label>
                        )}
                    </div>
                )}
                {!activeSearchText.trim() && (
                    <MenuConversations
                        items={chats.filter((c) => !c.draft)}
                        selectedItem={activeChat}
                        onRemoveConversation={handleRemoveConversation}
                        onConversationTitleChange={handleConversationTitleChange}
                        onConversationSelected={handleConversationSelected}
                        onPinToggle={handlePinToggle}
                        onLoadMore={fetchNextPage}
                        hasMore={hasMoreConversations}
                        isLoadingMore={isFetchingNextPage}
                        scrollContainerRef={conversationsScrollContainerRef}
                    />
                )}
            </div>
            
            {/* Bottom section - fixed at bottom */}
            <div style={{ flexShrink: 0 }}>
                <Divider className={classes.smallDivider} />
                <div className={classes.bottom}>
                    <MenuBottom 
                        name={userProfile?.name} 
                        photo={userProfile?.photo} 
                        onCloseMenuRequired={onCloseMenuRequired}
                    />
                </div>
            </div>
        </div>
    );
};
